import { AdviserRole } from './constants'

export interface OperatorIdentity {
  friendlyName: string
  firstName: string
  lastName: string
  avatarUrl: string | null
  teamLabel?: string | null
}

export interface CustomerCaseSummary {
  id: number
  status: 'open' | 'waiting' | 'done'
  waitingOn: 'customer' | 'team' | 'vendor' | null
  returnedToHaven?: boolean
  reopened?: boolean
  owner: OperatorIdentity | null
  lastHumanTurn:
    | { kind: 'customer'; at: string }
    | { kind: 'operator'; at: string; operator: OperatorIdentity | null }
    | null
}

export type CustomerCaseEvent = { caseId?: number } & (
  | { id: number; kind: 'handoff'; at: string; summary: string }
  | {
      id: number
      kind: 'assigned'
      at: string
      operator: OperatorIdentity | null
      previousOperator: OperatorIdentity | null
    }
  | {
      id: number
      kind: 'unassigned'
      at: string
      operator: OperatorIdentity | null
    }
  | {
      id: number
      kind: 'resolved'
      at: string
      resolvedBy: 'operator' | 'haven' | 'automation' | 'customer' | null
      resolver: OperatorIdentity | null
    }
  | { id: number; kind: 'reopened'; at: string }
)

export interface CustomerCaseEventPage {
  items: CustomerCaseEvent[]
  nextCursor: string | null
}

export type CustomerConversationActivity =
  | { kind: 'customer'; at: string }
  | { kind: 'haven'; at: string }
  | { kind: 'operator'; at: string; operator: OperatorIdentity | null }

export interface ConversationSummary {
  id: number
  userId: number
  title: string | null
  assignedOperatorId?: number | null
  holder?: OperatorIdentity | null
  createdAt: string
  updatedAt: string
  case?: CustomerCaseSummary | null
  lastActivity?: CustomerConversationActivity | null
  lastVisibleReplyAt?: string | null
}

export interface OperatorIdentity {
  friendlyName: string
  firstName: string
  lastName: string
  avatarUrl: string | null
}

export interface FeedbackRecord {
  messageId: number | null
  rating: number
  comment: string | null
}

export interface ConversationDetailResponse {
  conversation: ConversationSummary
  messages: AdviserMessage[]
  messagesPage?: { nextCursor: string | null }
  feedback?: FeedbackRecord[]
  caseEvents?: CustomerCaseEventPage
}

/** Mirrors the API's `WidgetDisplay` in `agent_core/types.ts`. */
export interface WidgetDisplay {
  label: string
  meta?: string
}

export type SavedPart =
  | { type: 'text'; text: string }
  | { type: 'tool-call'; id: string; tool: string; input?: unknown }
  | {
      type: 'tool-result'
      id: string
      tool: string
      output?: unknown
      error?: boolean
      display?: WidgetDisplay
    }

export interface AdviserMessage {
  id: number
  conversationId: number
  role: `${AdviserRole}`
  content: string
  sources?: CitationSource[]
  retrievalContext?: {
    fallback?: boolean
    parts?: SavedPart[]
    toolResults?: Array<{ toolName: string; result: unknown }>
  } | null
  createdAt: string
  operator?: OperatorIdentity | null
}

export interface CitationSource {
  articleId?: string
  title: string
  url: string
}

export interface FeedbackVote {
  rating: -1 | 1
  comment?: string
}

export interface FeedbackPayload extends FeedbackVote {
  messageId: number
}

export interface TicketComment {
  body: string
  createdAt: string
  authorType: 'requester' | 'agent'
  isPublic: boolean
}

export interface TicketSummary {
  ticketId: number
  subject: string
  status: string
  comments: TicketComment[]
  createdAt: string
  updatedAt: string
}

export interface ToolInvocation {
  toolName: string
  toolCallId?: string
  state: string
  result?: unknown
  input?: unknown
  /** Rehydrated from a stored turn, so its controls must stay read-only. */
  restored?: boolean
  display?: WidgetDisplay
}

export type ActiveView = 'chat' | 'history'

export type MessageTab = 'all' | 'awaiting' | 'with_heard' | 'resolved'
export type MessageThreadStatus = Exclude<MessageTab, 'all'>
export type MessageScope = 'tab' | 'search'

export interface MessageThreadCursor {
  page: number
  before: string | null
}

export interface MessageThreadPreview {
  author: 'heard' | 'you'
  text: string
}

export interface MessageThread {
  ticketId: number
  subject: string
  status: MessageThreadStatus
  preview: MessageThreadPreview | null
  updatedAt: string
}

export interface MessageThreadCounts {
  awaiting: number
  withHeard: number
  /** Payload only. The Resolved tab never renders a count pill. */
  resolved: number
}

export interface MessageThreadsResponse {
  scope: MessageScope
  defaultTab: MessageTab
  threads: MessageThread[]
  counts: MessageThreadCounts
  hasHistory: boolean
  nextCursor: MessageThreadCursor | null
}

export interface MessageAttachment {
  id: number
  fileName: string
  contentType: string
  size: number
  url: string | null
  thumbnailUrl?: string | null
}

export interface MessageThreadDetail {
  ticketId: number
  subject: string
  status: MessageThreadStatus
  updatedAt: string
  comments: Array<{
    author: 'heard' | 'you'
    body: string
    htmlBody?: string | null
    createdAt: string
    attachments?: MessageAttachment[]
  }>
}

/** Mirrors the API's `LAUNCH_ACTION` in `adviser/core/launch-contexts.ts`. */
export const HAVEN_LAUNCH_ACTION = {
  openTickets: 'open_tickets',
  quarterlyEstimates: 'quarterly_estimates',
  catchUpBookkeeping: 'catch_up_bookkeeping',
} as const

export const HAVEN_LAUNCH_ACTIONS = [
  HAVEN_LAUNCH_ACTION.openTickets,
  HAVEN_LAUNCH_ACTION.quarterlyEstimates,
  HAVEN_LAUNCH_ACTION.catchUpBookkeeping,
] as const
export type HavenLaunchAction = (typeof HAVEN_LAUNCH_ACTIONS)[number]

export const HAVEN_ENTRY_POINT = {
  sidebarMessages: 'sidebarMessages',
  welcomePrompt: 'welcomePrompt',
  queryParam: 'queryParam',
  taxesQuarterly: 'taxesQuarterly',
  transactionSorting: 'transactionSorting',
  onboardingSetupLanding: 'onboardingSetupLanding',
  sidebarAskHaven: 'sidebarAskHaven',
} as const

export type HavenEntryPoint =
  (typeof HAVEN_ENTRY_POINT)[keyof typeof HAVEN_ENTRY_POINT]

/** Mirrors `window` on the API's catch_up_months response. */
export interface CatchUpWindow {
  firstMonth: string
  lastMonth: string
  totalMonths: number
}

export type HavenLaunchContext =
  | { type: 'message'; text: string }
  | { type: 'action'; action: HavenLaunchAction }

export type HavenActionLaunchContext = Extract<
  HavenLaunchContext,
  { type: 'action' }
>

export interface AttachedFile {
  fileName: string
  contentType: string
  previewUrl?: string
}

export interface AttachedFilesMetadata {
  attachedFiles: AttachedFile[]
}

export interface PageContext {
  path: string
  section?: string
}
